/**
 * Editor session utilities — storage keys, recovery IDs, hash helpers.
 *
 * Extracted from EditorApp.tsx for reuse and testability.
 */
import type { TemplateVariation } from '../types/editor';
import type { UndoSnapshot } from './UndoManager';
import { stableStringify } from './snapshotHash';

// ── Storage keys ──────────────────────────────────────────────────────

export const SESSION_DIRTY_KEY = 'snaplayer.editor.session-dirty';
export const CLEAN_SHUTDOWN_KEY = 'snaplayer.editor.clean-shutdown'; // '1' = last session closed cleanly
export const LAST_SAVED_AT_PREFIX = 'snaplayer.editor.last-saved-at:'; // + templateId
export const ACTIVE_TEMPLATE_STORAGE_KEY = 'snaplayer.editor.active-template';
export const RECOVERY_CLEARED_STORAGE_KEY = 'snaplayer.editor.recovery.hidden';
export const IMAGE_LIBRARY_EVENT = 'snaplayer:image-library:updated';
export const DEFAULT_SUPABASE_PROJECT_REF = 'zlcizetehzipmetzesox';
export const ACTIVE_TEMPLATE_LIMIT = 5;

// ── ID generation ─────────────────────────────────────────────────────

export function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const prefix = 'snap';
  const timestamp = Date.now().toString(16);
  const randomPart = Math.random().toString(16).slice(2, 10);
  return `${prefix}-${timestamp}-${randomPart}`;
}

export const generateRecoveredTemplateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `tpl-${Date.now()}-${randomSuffix}`;
};

// ── Hashing ───────────────────────────────────────────────────────────

export const hashString = (value: string): string => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return (hash >>> 0).toString(16);
};

// ── Undo helpers ──────────────────────────────────────────────────────

export const computeUndoStackHash = (stack: Array<UndoSnapshot<any>>): string => {
  if (!Array.isArray(stack) || stack.length === 0) {
    return '0';
  }
  try {
    const payload = stack.map((entry) => ({
      label: entry.label ?? null,
      timestamp: entry.timestamp,
      state: entry.state,
    }));
    const serialized = stableStringify(payload);
    return hashString(serialized);
  } catch (error) {
    console.warn('computeUndoStackHash failed', error);
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 8)}`;
  }
};

export const cloneVariationsForUndo = (source: TemplateVariation[]): TemplateVariation[] => {
  try {
    return JSON.parse(JSON.stringify(source)) as TemplateVariation[];
  } catch (error) {
    console.warn('cloneVariationsForUndo failed; using fallback clone', error);
    return source.map((variation) => ({
      ...variation,
      layers: Array.isArray(variation.layers)
        ? variation.layers.map((layer) =>
          layer && typeof layer === 'object' ? { ...(layer as Record<string, unknown>) } : layer,
        )
        : [],
      variables: variation.variables ? { ...variation.variables } : {},
      snap_rows: Array.isArray(variation.snap_rows)
        ? variation.snap_rows.map((row) => ({
          index: typeof row?.index === 'number' ? row.index : 0,
          data: row?.data && typeof row.data === 'object' ? { ...(row.data as Record<string, unknown>) } : {},
          images:
            row?.images && typeof row.images === 'object'
              ? { ...(row.images as Record<string, unknown>) }
              : {},
        }))
        : [],
    }));
  }
};
